/**
 * One Mate container — keyed by its target, `projectId:serviceId` — as one pure machine (DESIGN
 * §4.5): `transitionContainer(state, event, ctx) → { state, effects }`.
 *
 * - **Levels change only on read facts**: a platform status push, a process the activity feed
 *   reports, a probe reading, a link connect, the Mate flag's read, or an intent our own verb
 *   created. A cap past its budget sets `overdue` on the current level and changes nothing else
 *   (MC-13, I13). A level probes read is late only once a reading sent since its cap ran out still
 *   finds it not up: its cap running out asks for one (`probe`), and a tab that reads nothing — a
 *   hidden one pauses its probes — never calls a Mate that may have answered late.
 * - A reading counts for a level only if its probe was sent after the level began: the old server
 *   answering before a restart takes it down never ends that restart.
 * - Our restart shows `restarting(you)` and our update `updating`, from the verb's acceptance
 *   until a read fact proves the container back: a link connect after the intent, a changed
 *   `/healthz` `initAt`, a reading sent after the platform's own restart process ended, or — for
 *   an update — a descriptor on another version.
 * - A live socket outranks every guess but `inactive`: a connect ends booting at once, and a
 *   connect since the platform began restarting ends that restart while its status still says
 *   RESTARTING. A ready container no socket holds stays ready through `READY_SILENCE_MS` of
 *   unanswered probes, long enough for the platform to say why; silent past it, it is a boot
 *   that only failed probes suggest — `guessed`, read at the backing-off intervals, and capped.
 */
import type { ExecutionEnvironmentUpdate } from "@t3tools/contracts";
import type { Instant } from "./exchange.ts";
import type { ContainerVerdict } from "./environmentMachine.ts";
import type { ProbeCadence, ProbeReading } from "./probe.ts";

// ── Facts ─────────────────────────────────────────────────────────────────────────────────────

/** The platform's statuses for the target's project and its zcp service; null service = unread. */
export interface PlatformStatus {
  readonly project: string;
  readonly service: string | null;
  /** When the zcp service was made, as the listing reads it: a first build's wait runs from it. */
  readonly serviceCreated?: string;
}

/** `ZCP_MATE_ENABLED` as read; `"unknown"` when the read could not say. */
export type MateFlag = boolean | "unknown";

/** A local, time-boxed expectation our own verb created (C8). */
export type ContainerIntent =
  | {
      readonly kind: "restart" | "enable" | "upgrade-restart";
      readonly since: Instant;
      /** The `/healthz` `initAt` read just before the verb was sent; absent when it could not say. */
      readonly initAt?: string;
    }
  /** `from` is the server version the update started on; null when it was not known. */
  | { readonly kind: "update"; readonly since: Instant; readonly from: string | null };

export type IntentKind = ContainerIntent["kind"];

/**
 * The `/healthz` `initAt` a restart started from, read before it began — the verb's own read, or
 * the reading held then — so a re-init reads as a change and never as a comparison of the
 * container's clock with the browser's: `held` (another value is a re-init), `absent` (the
 * container served no `/mate/healthz`: any value is), or `unread` (nothing said it: a value plainly
 * later than the start is a re-init, and otherwise only the platform's word or a connect ends the
 * restart). A read sent after the restart began is never its baseline: it may already be the new
 * server's.
 */
export type InitAtBaseline =
  | { readonly kind: "held"; readonly initAt: string }
  | { readonly kind: "absent" }
  | { readonly kind: "unread" };

// ── Levels ────────────────────────────────────────────────────────────────────────────────────

export type ContainerLevel =
  | { readonly level: "unknown" }
  | { readonly level: "ready" }
  | { readonly level: "creating"; readonly since: Instant }
  | { readonly level: "provisioning"; readonly since: Instant }
  | {
      readonly level: "booting";
      readonly since: Instant;
      /**
       * Only failed probes say it is coming up: no platform transition, no process, no `/healthz`
       * answering. Evidence of any of those clears it for the rest of the boot.
       */
      readonly guessed: boolean;
    }
  | {
      readonly level: "restarting";
      readonly by: "platform" | "you";
      readonly since: Instant;
      readonly baseline: InitAtBaseline;
      /** When the platform reported its restart process over; null while it has not. */
      readonly platformEnded: Instant | null;
    }
  | { readonly level: "updating"; readonly since: Instant; readonly from: string | null }
  | { readonly level: "needs-enable" }
  | { readonly level: "needs-update" }
  | { readonly level: "not-yet-available" }
  | { readonly level: "inactive"; readonly status: string };

export interface ContainerMachine {
  readonly state: ContainerLevel;
  /** The current level's cap ran out; only a new level (or a new `since` on it) clears it. */
  readonly overdue: boolean;
  readonly platform: PlatformStatus | null;
  /** When the platform's service status turned to restarting; null while it is not. */
  readonly platformRestartAt: Instant | null;
  /** A platform process (a start, a restart) is running against the container. */
  readonly processRunning: boolean;
  /** When the last running process was seen ending; null while none has. */
  readonly processEndedAt: Instant | null;
  /** The newest probe reading and when its probe was sent; null before one. */
  readonly reading: { readonly reading: ProbeReading; readonly sentAt: Instant } | null;
  /**
   * When something began proving the container up: its socket, or HQ holding its Mate online
   * (`containerStore`). Null while nothing does.
   */
  readonly connectedSince: Instant | null;
  /**
   * When the container was last known up outside a socket: the latest probe it answered ready
   * left, or its socket dropped. Null before either.
   */
  readonly upAt: Instant | null;
  /** Read only for a container that predates Mate; null before the read. */
  readonly mateFlag: MateFlag | null;
  /** Our verb's expectation while its level holds; null once a read fact settled it. */
  readonly intent: ContainerIntent | null;
  /** A restart of ours already ended with the container still predating Mate. */
  readonly restartTried: boolean;
  /**
   * The version its connected server said an automatic update is moving it from; null while none
   * is under way. Its link dropping then is the update's restart (`UPDATE_UNDERWAY`).
   */
  readonly updateUnderway: string | null;
  /** When the current cap runs out; null when no cap is running. */
  readonly timer: Instant | null;
}

export type ContainerEvent =
  | { readonly type: "PLATFORM"; readonly status: PlatformStatus }
  | { readonly type: "PROCESS"; readonly running: boolean }
  | { readonly type: "PROBED"; readonly reading: ProbeReading; readonly sentAt: Instant }
  | { readonly type: "LINK"; readonly connected: boolean }
  | { readonly type: "MATE_FLAG"; readonly flag: MateFlag }
  | { readonly type: "INTENT"; readonly intent: ContainerIntent }
  /** Its connected server's word on an automatic update: under way from `from`, or none (null). */
  | { readonly type: "UPDATE_UNDERWAY"; readonly from: string | null }
  | { readonly type: "TICK" };

export const CONTAINER_TIMER_KEY = "container";

/** The automatic update's phases before its restart: the server stops at the end of them. */
const UPDATE_BEFORE_RESTART: ReadonlySet<string> = new Set(["staging", "draining", "switching"]);

/**
 * The version a server's automatic update is moving it from while one is under way (`zcp mate
 * status` relayed in its descriptor's `update`), else null: what `UPDATE_UNDERWAY` carries.
 */
export const automaticUpdateFrom = (
  update: ExecutionEnvironmentUpdate | null | undefined,
): string | null => {
  const automatic = update?.automatic;
  if (automatic !== undefined && UPDATE_BEFORE_RESTART.has(automatic.phase))
    return automatic.runningVersion;
  // The Mate's own container restart (zcp behind): the server ends with the container.
  const restart = update?.restart?.phase;
  return update != null && (restart === "draining" || restart === "restarting")
    ? update.installed
    : null;
};

export type ContainerEffect =
  | {
      readonly kind: "schedule";
      readonly key: typeof CONTAINER_TIMER_KEY;
      readonly at: Instant;
      readonly event: { readonly type: "TICK" };
    }
  | { readonly kind: "cancel"; readonly key: typeof CONTAINER_TIMER_KEY }
  /** Read `ZCP_MATE_ENABLED`: a container predating Mate is Enable only when the flag is off. */
  | { readonly kind: "read-mate-flag" }
  /** Read the container now: its cap ran out, and only a reading since says whether it is late. */
  | { readonly kind: "probe" };

export interface ContainerContext {
  readonly now: Instant;
}

// ── Caps ──────────────────────────────────────────────────────────────────────────────────────

/**
 * How long each waiting level is given before it is `overdue`. Booting's 90 s runs from the moment
 * the last process ends; an intent's budget is its level's cap, run from the verb's acceptance.
 */
export const CONTAINER_CAPS_MS = {
  creating: 60_000,
  provisioning: 300_000,
  booting: 90_000,
  /** The platform's own restart, and ours. */
  restarting: 180_000,
  updating: 120_000,
} as const;

/**
 * How long a ready container no socket holds may leave probes unanswered before it counts as
 * booting: the platform's own restart push lands within it (gate F measured ~4 s).
 */
export const READY_SILENCE_MS = 15_000;

export const initialContainer = (): ContainerMachine => ({
  state: { level: "unknown" },
  overdue: false,
  platform: null,
  platformRestartAt: null,
  processRunning: false,
  processEndedAt: null,
  reading: null,
  connectedSince: null,
  upAt: null,
  mateFlag: null,
  intent: null,
  restartTried: false,
  updateUnderway: null,
  timer: null,
});

// ── Projections ───────────────────────────────────────────────────────────────────────────────

/** The verdict region C of the environment machine holds (§4.4). */
export const containerVerdict = (machine: ContainerMachine): ContainerVerdict => {
  const { state, overdue } = machine;
  switch (state.level) {
    case "unknown":
    case "ready":
    case "needs-enable":
    case "needs-update":
    case "not-yet-available":
      return { level: state.level };
    case "inactive":
      return { level: "inactive", status: state.status };
    case "booting":
      return state.guessed
        ? { level: "booting", overdue, guessed: true }
        : { level: "booting", overdue };
    case "creating":
    case "provisioning":
    case "updating":
      return { level: state.level, overdue };
    case "restarting":
      return { level: "restarting", by: state.by, overdue };
  }
};

/**
 * How often the probe store reads this container (§4.5 probes): every 2 s while it comes up,
 * backing off past its cap or while a ready one leaves them unanswered, never while a socket
 * proves it up, and otherwise only when a push, a failure or a wake asks. A boot nothing vouches
 * for — only failed probes say it, as of a dead Mate or a zcp serving none — is polled only while
 * someone waits on it (`watched`: the route's, the screen's, a lease's), on the backing-off ladder.
 */
export const probeCadence = (machine: ContainerMachine, watched = false): ProbeCadence => {
  if (platformSaysDown(machine)) return { kind: "none" };
  switch (machine.state.level) {
    case "booting":
      if (machine.state.guessed) return watched ? { kind: "poll", overdue: true } : ON_DEMAND;
      return { kind: "poll", overdue: machine.overdue };
    case "restarting":
    case "updating":
      return { kind: "poll", overdue: machine.overdue };
    case "ready":
      if (machine.connectedSince !== null) return { kind: "none" };
      return unansweredSinceUp(machine) ? { kind: "poll", overdue: true } : { kind: "on-demand" };
    case "unknown":
    case "needs-enable":
    case "needs-update":
    case "not-yet-available":
      return { kind: "on-demand" };
    case "creating":
    case "provisioning":
    case "inactive":
      return { kind: "none" };
  }
};

const ON_DEMAND: ProbeCadence = { kind: "on-demand" };

/**
 * The platform's own status says the Mate's server is not up: its project or its zcp service is
 * read and is not ACTIVE (restarting, upgrading, stopped…). Its address is then neither probed nor
 * connected to — the balancer answers for a server that is down without CORS headers, so every
 * read is a console error and a wasted request — and the status push that says ACTIVE again reads
 * it. A status not read yet says nothing.
 */
export const platformSaysDown = (machine: ContainerMachine): boolean => {
  const platform = machine.platform;
  if (platform === null) return false;
  return (
    platform.project !== "ACTIVE" || (platform.service !== null && platform.service !== "ACTIVE")
  );
};

/** The level is one probes read on their own ladder: a reading can prove it up. */
const readByProbes = (machine: ContainerMachine): boolean => probeCadence(machine).kind === "poll";

/** The held reading is an unanswered probe sent since the container was last known up. */
export const unansweredSinceUp = (machine: ContainerMachine): boolean =>
  (machine.reading?.reading.kind === "unreachable" ||
    machine.reading?.reading.kind === "not-answering") &&
  (machine.upAt === null || notBefore(machine.reading.sentAt, machine.upAt));

// ── Transition ────────────────────────────────────────────────────────────────────────────────

const PROJECT_CREATING: ReadonlySet<string> = new Set(["NEW", "CREATING"]);
/**
 * A zcp service on its way up. `READY_TO_DEPLOY` is its first build: a Mate's container is never
 * deployed by hand, so one with no app version yet waits for the build its import started — it is
 * coming up, never "not running" (measured 2026-10-02: 45 s of an Add read that way).
 */
const SERVICE_PROVISIONING: ReadonlySet<string> = new Set([
  "NEW",
  "CREATING",
  "STARTING",
  "READY_TO_DEPLOY",
]);
const SERVICE_RESTARTING: ReadonlySet<string> = new Set(["RESTARTING", "UPGRADING", "RELOADING"]);

const after = (instant: Instant, ms: number): Instant => ({
  wall: instant.wall + ms,
  mono: instant.mono + ms,
});

/** True once either clock has passed `at`. */
const reached = (at: Instant, now: Instant): boolean => now.wall >= at.wall || now.mono >= at.mono;

/** `a` is at or after `b` on both clocks. */
const notBefore = (a: Instant, b: Instant): boolean => a.wall >= b.wall && a.mono >= b.mono;

/** The link connected after `since`: a socket held from before proves nothing about it. */
const connectedAfter = (machine: ContainerMachine, since: Instant): boolean =>
  machine.connectedSince !== null &&
  notBefore(machine.connectedSince, since) &&
  machine.connectedSince.mono > since.mono;

const sameJson = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

/** The instant the current level's cap runs from; null for a level without a cap. */
const capStart = (machine: ContainerMachine): Instant | null => {
  const state = machine.state;
  switch (state.level) {
    case "creating":
    case "provisioning":
    case "restarting":
    case "updating":
      return state.since;
    case "booting": {
      if (machine.processRunning) return null;
      const ended = machine.processEndedAt;
      return ended !== null && notBefore(ended, state.since) ? ended : state.since;
    }
    default:
      return null;
  }
};

const capMs = (level: ContainerLevel["level"]): number | null =>
  level in CONTAINER_CAPS_MS ? CONTAINER_CAPS_MS[level as keyof typeof CONTAINER_CAPS_MS] : null;

/** The one timer: the cap's end while the level is not yet overdue. */
const withTimer = (machine: ContainerMachine): ContainerMachine => {
  const start = machine.overdue ? null : capStart(machine);
  const ms = capMs(machine.state.level);
  const timer = start === null || ms === null ? null : after(start, ms);
  return sameJson(timer, machine.timer) ? machine : { ...machine, timer };
};

const sinceOf = (state: ContainerLevel): Instant | null => ("since" in state ? state.since : null);

/**
 * Moves to `next`, clearing `overdue` when a new level begins: another level, or the same one
 * from a new `since` (an intent of ours on the level the container is already on).
 */
const moveTo = (machine: ContainerMachine, next: ContainerLevel): ContainerMachine => {
  if (sameJson(machine.state, next)) return machine;
  const begins =
    machine.state.level !== next.level || !sameJson(sinceOf(machine.state), sinceOf(next));
  return { ...machine, state: next, overdue: begins ? false : machine.overdue };
};

/** A reading counts for the current level only if its probe left after the level began. */
const readingSince = (machine: ContainerMachine, since: Instant): ProbeReading | null =>
  machine.reading !== null && notBefore(machine.reading.sentAt, since)
    ? machine.reading.reading
    : null;

/** The baseline a restart beginning now starts from, out of the reading held. */
const baselineOf = (machine: ContainerMachine): InitAtBaseline => {
  const reading = machine.reading?.reading;
  if (reading?.kind === "predates-mate") return { kind: "absent" };
  return (reading?.kind === "ready" ||
    reading?.kind === "initializing" ||
    reading?.kind === "not-answering") &&
    reading.initAt !== null
    ? { kind: "held", initAt: reading.initAt }
    : { kind: "unread" };
};

/**
 * Where the facts put a running container that no restart or update holds. `reading` is the held
 * one, or null when none counts; a boot it starts runs from when its probe was sent.
 */
const fromReading = (machine: ContainerMachine, reading: ProbeReading | null): ContainerLevel => {
  if (machine.connectedSince !== null) return { level: "ready" };
  const current = machine.state;
  if (reading === null || machine.reading === null) return current;
  switch (reading.kind) {
    case "ready":
      return { level: "ready" };
    case "initializing":
      return booting(machine, false);
    case "unreachable":
      // A container that was up is given `READY_SILENCE_MS` for the platform's status to say
      // why; the link meanwhile says it reconnects (§4.4 row 10).
      return current.level === "ready" && !silent(machine) ? current : booting(machine, true);
    case "not-answering": {
      if (current.level === "ready" && !silent(machine)) return current;
      // zcp's init is complete and its Mate does not answer: whatever vouched for a boot (its
      // init running) is over, and only the Mate's silence is left — a guess, which its link's
      // words speak for, never a start. A process the platform runs still says it starts.
      const level = booting(machine, true);
      return level.level === "booting" && !machine.processRunning
        ? { ...level, guessed: true }
        : level;
    }
    case "predates-mate":
      // A restart of ours already came back to this: the zcp release there does not carry Mate.
      if (machine.restartTried) return { level: "not-yet-available" };
      // Unread or unreadable, the flag keeps the container booting: never Enable on a guess.
      if (machine.mateFlag === false) return { level: "needs-enable" };
      return machine.mateFlag === true ? { level: "needs-update" } : booting(machine, true);
  }
};

/** The held probe went unanswered `READY_SILENCE_MS` or more after the container was last up. */
const silent = (machine: ContainerMachine): boolean =>
  machine.upAt === null ||
  notBefore(machine.reading!.sentAt, after(machine.upAt, READY_SILENCE_MS));

/**
 * The boot a reading puts the container in, from when its probe was sent. `guessed` when the
 * reading says nothing of Mate coming up; a restart that just ended, or a process the platform
 * runs against it, says it is.
 */
const booting = (machine: ContainerMachine, guessed: boolean): ContainerLevel => {
  const current = machine.state;
  const guess = guessed && current.level !== "restarting" && !machine.processRunning;
  if (current.level === "booting") return guess ? current : { ...current, guessed: false };
  return { level: "booting", since: machine.reading!.sentAt, guessed: guess };
};

/** A restart of ours or the platform's is over once a read fact proves the container back. */
const restartOver = (
  machine: ContainerMachine,
  state: Extract<ContainerLevel, { readonly level: "restarting" }>,
): ProbeReading | "connected" | null => {
  if (connectedAfter(machine, state.since)) return "connected";
  const reading = readingSince(machine, state.since);
  if (reading === null) return null;
  // The platform's word that it is over: its status leaving RESTARTING, or its restart's process
  // ending, which a tab that never saw that status still reads.
  const ended = state.platformEnded ?? processEndedSince(machine, state.since);
  if (ended !== null && notBefore(machine.reading!.sentAt, ended)) return reading;
  const initAt = readInitAt(reading);
  if (initAt === null) return null;
  const baseline = state.baseline;
  const reinitialized =
    baseline.kind === "absent" ||
    (baseline.kind === "held" ? initAt !== baseline.initAt : Date.parse(initAt) > state.since.wall);
  return reinitialized ? reading : null;
};

/** When a process the platform ran against the container ended since `since`; null otherwise. */
const processEndedSince = (machine: ContainerMachine, since: Instant): Instant | null =>
  !machine.processRunning &&
  machine.processEndedAt !== null &&
  notBefore(machine.processEndedAt, since)
    ? machine.processEndedAt
    : null;

const readInitAt = (reading: ProbeReading): string | null =>
  reading.kind === "ready" || reading.kind === "initializing" || reading.kind === "not-answering"
    ? reading.initAt
    : null;

const updateOver = (
  machine: ContainerMachine,
  state: Extract<ContainerLevel, { readonly level: "updating" }>,
): boolean => {
  if (connectedAfter(machine, state.since)) return true;
  const reading = readingSince(machine, state.since);
  return (
    reading?.kind === "ready" &&
    state.from !== null &&
    reading.descriptor.serverVersion !== state.from
  );
};

/**
 * When a first build's wait began: the service's own creation, so every tab times it the same —
 * one opened twenty minutes into a build that failed says at once that it is taking longer. Null
 * where the listing gave no time, or one ahead of now.
 */
const firstBuildSince = (platform: PlatformStatus, now: Instant): Instant | null => {
  const created = Date.parse(platform.serviceCreated ?? "");
  if (Number.isNaN(created) || created > now.wall) return null;
  return { wall: created, mono: now.mono - (now.wall - created) };
};

/** The level the facts held now put the container at; an intent lives only on its own level. */
const settleLevel = (machine: ContainerMachine, now: Instant): ContainerMachine => {
  const next = settleFacts(machine, now);
  const holds = next.state.level === "restarting" || next.state.level === "updating";
  return next.intent === null || holds ? next : { ...next, intent: null };
};

const settleFacts = (machine: ContainerMachine, now: Instant): ContainerMachine => {
  const platform = machine.platform;
  const state = machine.state;
  if (platform !== null) {
    if (PROJECT_CREATING.has(platform.project)) {
      return moveTo(
        machine,
        state.level === "creating" ? state : { level: "creating", since: now },
      );
    }
    if (platform.project === "STARTING") {
      return moveTo(
        machine,
        state.level === "provisioning" ? state : { level: "provisioning", since: now },
      );
    }
    if (platform.project !== "ACTIVE") {
      return moveTo(machine, { level: "inactive", status: platform.project });
    }
    const service = platform.service;
    if (service !== null && SERVICE_PROVISIONING.has(service)) {
      const since = state.level === "provisioning" ? state.since : now;
      const built = service === "READY_TO_DEPLOY" ? firstBuildSince(platform, now) : null;
      return moveTo(machine, {
        level: "provisioning",
        since: built !== null && built.wall < since.wall ? built : since,
      });
    }
    if (service !== null && SERVICE_RESTARTING.has(service)) {
      // A socket connected since the platform began restarting outranks the status it still
      // shows (§0A): the restart is over, and the reads say so.
      const restartAt = machine.platformRestartAt;
      if (restartAt !== null && connectedAfter(machine, restartAt)) return settleReads(machine);
      if (state.level === "restarting") {
        return moveTo(
          machine,
          state.platformEnded === null ? state : { ...state, platformEnded: null },
        );
      }
      if (state.level === "updating") return machine;
      const intent = machine.intent;
      return moveTo(machine, {
        level: "restarting",
        by: intent === null ? "platform" : "you",
        since: intent === null ? now : intent.since,
        baseline: baselineOf(machine),
        platformEnded: null,
      });
    }
    if (service !== null && service !== "ACTIVE") {
      return moveTo(machine, { level: "inactive", status: service });
    }
    if (service === null && (state.level === "creating" || state.level === "provisioning")) {
      return machine;
    }
    if (
      state.level === "creating" ||
      state.level === "provisioning" ||
      state.level === "inactive"
    ) {
      // The platform brought it up: only a probe sent from now on says how far Mate got.
      return moveTo(machine, { level: "booting", since: now, guessed: false });
    }
  }
  return settleReads(machine);
};

/** Where the reads put a container the platform's status leaves to them. */
const settleReads = (machine: ContainerMachine): ContainerMachine => {
  const state = machine.state;
  switch (state.level) {
    case "restarting": {
      const over = restartOver(machine, state);
      if (over === null) return machine;
      const settled: ContainerMachine = {
        ...machine,
        restartTried: machine.intent !== null || machine.restartTried,
      };
      return moveTo(
        settled,
        over === "connected" ? { level: "ready" } : fromReading(settled, over),
      );
    }
    case "updating":
      return updateOver(machine, state) ? moveTo(machine, { level: "ready" }) : machine;
    case "booting":
      return moveTo(machine, fromReading(machine, readingSince(machine, state.since)));
    default:
      return moveTo(machine, fromReading(machine, machine.reading?.reading ?? null));
  }
};

/** The intent our verb created: its level holds from `since` until a read fact settles it. */
const intend = (machine: ContainerMachine, intent: ContainerIntent): ContainerMachine => {
  // Enable writes the flag it would otherwise be read for: the held read no longer says.
  const next: ContainerMachine = {
    ...machine,
    intent,
    mateFlag: intent.kind === "enable" ? null : machine.mateFlag,
  };
  if (intent.kind === "update") {
    return moveTo(next, { level: "updating", since: intent.since, from: intent.from });
  }
  return moveTo(next, {
    level: "restarting",
    by: "you",
    since: intent.since,
    baseline:
      intent.initAt === undefined ? baselineOf(machine) : { kind: "held", initAt: intent.initAt },
    platformEnded: null,
  });
};

const apply = (
  machine: ContainerMachine,
  event: ContainerEvent,
  now: Instant,
): ContainerMachine => {
  switch (event.type) {
    case "PLATFORM": {
      if (sameJson(machine.platform, event.status)) return machine;
      const wasRestarting = SERVICE_RESTARTING.has(machine.platform?.service ?? "");
      const restarting = SERVICE_RESTARTING.has(event.status.service ?? "");
      const next = {
        ...machine,
        platform: event.status,
        platformRestartAt: restarting ? (machine.platformRestartAt ?? now) : null,
      };
      // The platform's restart ending is ours to see only once it was seen running.
      const state = machine.state;
      if (
        state.level === "restarting" &&
        state.platformEnded === null &&
        wasRestarting &&
        event.status.service === "ACTIVE"
      ) {
        return settleLevel(moveTo(next, { ...state, platformEnded: now }), now);
      }
      return settleLevel(next, now);
    }
    case "PROCESS": {
      if (event.running === machine.processRunning) return machine;
      return settleLevel(
        {
          ...machine,
          processRunning: event.running,
          processEndedAt: event.running ? machine.processEndedAt : now,
        },
        now,
      );
    }
    case "PROBED": {
      const up =
        event.reading.kind === "ready" &&
        (machine.upAt === null || notBefore(event.sentAt, machine.upAt));
      return settleLevel(
        {
          ...machine,
          reading: { reading: event.reading, sentAt: event.sentAt },
          upAt: up ? event.sentAt : machine.upAt,
        },
        now,
      );
    }
    case "LINK": {
      const connected = machine.connectedSince !== null;
      if (event.connected === connected) return machine;
      const linked = settleLevel(
        {
          ...machine,
          connectedSince: event.connected ? now : null,
          upAt: event.connected ? machine.upAt : now,
        },
        now,
      );
      // The server said its update was under way: its link dropping is that update's restart,
      // held as one until it is back or its budget runs out. It speaks for this one drop.
      if (event.connected || machine.updateUnderway === null) return linked;
      const updating = settleLevel(
        intend(
          { ...linked, updateUnderway: null },
          { kind: "update", since: now, from: machine.updateUnderway },
        ),
        now,
      );
      return updating.intent === null ? { ...linked, updateUnderway: null } : updating;
    }
    case "UPDATE_UNDERWAY":
      return event.from === machine.updateUnderway
        ? machine
        : { ...machine, updateUnderway: event.from };
    case "MATE_FLAG":
      return event.flag === machine.mateFlag
        ? machine
        : settleLevel({ ...machine, mateFlag: event.flag }, now);
    case "INTENT": {
      // An intent the platform's facts already overrule (a project still creating, a service
      // stopped) has no level to hold, and changes nothing.
      const next = settleLevel(intend(machine, event.intent), now);
      return next.intent === null ? machine : next;
    }
    case "TICK":
      return machine;
  }
};

export function transitionContainer(
  machine: ContainerMachine,
  event: ContainerEvent,
  ctx: ContainerContext,
): { readonly state: ContainerMachine; readonly effects: ReadonlyArray<ContainerEffect> } {
  const { now } = ctx;
  const lapsed = (held: ContainerMachine): held is ContainerMachine & { timer: Instant } =>
    held.timer !== null && reached(held.timer, now);
  // Timers are hints: whatever the event, a cap that has run out on a level no probe reads is
  // overdue first.
  const due =
    lapsed(machine) && !readByProbes(machine)
      ? { ...machine, overdue: true, timer: null }
      : machine;
  let next = withTimer(apply(due, event, now));
  const effects: Array<ContainerEffect> = [];
  // A level probes read is overdue once a reading sent since its cap ran out leaves it on that
  // level; until one is, its cap asks for one. "Since" uses the same clocks as "ran out": after a
  // sleep pauses the monotonic clock, a reading sent past the cap on the wall clock counts.
  if (lapsed(next) && readByProbes(next)) {
    if (next.reading !== null && reached(next.timer, next.reading.sentAt)) {
      next = { ...next, overdue: true, timer: null };
    } else {
      effects.push({ kind: "probe" });
    }
  }
  if (!sameJson(next.timer, machine.timer)) {
    effects.push(
      next.timer === null
        ? { kind: "cancel", key: CONTAINER_TIMER_KEY }
        : { kind: "schedule", key: CONTAINER_TIMER_KEY, at: next.timer, event: { type: "TICK" } },
    );
  }
  const predates = next.reading?.reading.kind === "predates-mate";
  const predatedBefore = machine.reading?.reading.kind === "predates-mate";
  if (predates && next.mateFlag === null && !(predatedBefore && machine.mateFlag === null)) {
    effects.push({ kind: "read-mate-flag" });
  }
  return { state: next === machine ? machine : next, effects };
}
